import React from 'react';
import { Alert, Button, Modal, message } from 'antd';
import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';

import { NormalizedPrize } from './LuckyWheelContainer';
import { useSpinLuckyWheelMutation } from '@hooks/luckywheel/luckyWheel.gql.generated';
import { getCompanyBasedRoute } from '@utils/routeConstants';

import s from './LuckyWheel.module.less';
import debounce from 'lodash/debounce';
import { WHEEL_COLORS } from './util';
import { useAppLayout } from '@context/LayoutContext';
import WheelComponent from './WheelComponent';
import { useTranslation } from 'next-i18next';

interface ILuckyWheelProps {
  campaignId: string;
  prizes?: NormalizedPrize[];
  winningPrize?: Pick<NormalizedPrize, 'name'> | null;
  showBuyMore: boolean;
  noMoreSpin: boolean;
  reset: () => void;
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
}

function LuckyWheel(props: ILuckyWheelProps) {
  const { t: tLayout } = useTranslation('layout');
  const { isMobile } = useAppLayout();

  const { campaignId, prizes = [], reset, showBuyMore, setVisible, noMoreSpin } = props;
  const prizesById = [...prizes].sort((a, b) => Number.parseInt(b.id) - Number.parseInt(a.id));

  let segments = prizesById.map(e => e.name);
  let segColors = WHEEL_COLORS.slice(0, segments.length);

  if (segments.length > 0) {
    const bestGift = segments.shift();
    const firstColor = segColors.shift();
    if (segments.length <= 5) {
      segments = segments.concat(segments);
      segColors = segColors.concat(segColors);
    }
    segments.unshift(bestGift ?? '');
    segColors.unshift(firstColor ?? '');
  }

  const apolloClient = useApolloClient();
  const router = useRouter();

  const [winner, setWinner] = React.useState<string>('');
  const [isRunning, setIsRunning] = React.useState<boolean>(false);

  const [spin, { data: spinData, loading: loadingSpin }] = useSpinLuckyWheelMutation({
    variables: {
      input: {
        campaignId,
      },
    },
    onCompleted: data => {
      if (!data) {
        return;
      }

      const winningPrize = data.spinLuckyWheel;
      setWinner(winningPrize?.name || '');
      setIsRunning(true);
    },
    onError: error => message.error(error.message),
  });

  async function handleRotate() {
    await spin();
  }

  const handleSpinEnd = debounce((currentSegment: string) => {
    setIsRunning(false);
    Modal.success({
      icon: null,
      title: null,
      width: 600,
      closable: true,
      content: (
        <div className={s.notificationModal}>
          <p>{tLayout('spin.congratulations_on_receiving_gift')}</p>
          <p style={{ color: 'red', fontSize: '30px' }}>{spinData?.spinLuckyWheel?.name || currentSegment}</p>
        </div>
      ),
      onOk() {
        reset();
      },
      okText: tLayout('close'),
    });

    apolloClient.refetchQueries({
      include: ['LuckyWheelCampaignDetails', 'LuckyWheelHistory'],
    });
  }, 2000);

  function handleBuyToSpin() {
    setVisible?.(false);
    const campaignUrl = getCompanyBasedRoute(`instantdeal?gift_campaign_id=${campaignId}`);
    router.push(campaignUrl);
  }

  const onFinished = (currentSegment: string) => {
    handleSpinEnd(currentSegment);
  };

  return (
    <div className={s.luckyWheelRoot}>
      <div className={s.luckyWheel}>
        <WheelComponent
          isRunning={isRunning}
          segments={segments}
          segColors={segColors}
          winningSegment={winner}
          onFinished={currentSegment => onFinished(currentSegment)}
          primaryColor="#f57747"
          primaryColoraround="#f57747"
          buttonText=""
          isOnlyOnce={false}
          size={isMobile ? 170 : 250}
          upDuration={1000}
          downDuration={1500}
          fontFamily="Roboto"
        />
      </div>
      {showBuyMore && (
        <div className={s.alert}>
          <Alert
            type="warning"
            message={
              <div style={{ display: 'flex' }}>
                <p>{tLayout('spin.end_of_turn')}</p>
                <Button type="primary" size="small" style={{ marginLeft: '8px' }} onClick={handleBuyToSpin}>
                  {tLayout('spin.buy_more')}
                </Button>
              </div>
            }
            showIcon
          />
        </div>
      )}
      {!noMoreSpin && (
        <div className={s.actionButtons}>
          <Button type="primary" size={`large`} onClick={handleRotate} disabled={loadingSpin || isRunning}>
            {tLayout('spin.spin_prize')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default LuckyWheel;
